import isEqual from 'react-fast-compare';
import produce from 'immer';
import get from 'lodash/get';
import set from 'lodash/set';
import { combineReducers } from 'redux';

import type { Action } from 'Lib/reduxType';
import uuid from 'Lib/utils/uuid';

import { CLOSE_MODAL, MODAL_PROPS_PATH, OPEN_MODAL } from './constants';
import initialState from './initialState';
import {
  selectLastModalFromQueue,
  selectModalPropsForModal,
  setModalProps,
  unsetModalProps,
} from './selectors';
import type { ModalInQueue, ModalState } from './types';

export const queueReducer = produce<ModalState['queue'], [Action]>((draft, action) => {
  switch (action.type) {
    case OPEN_MODAL: {
      const modalProps = get(action.payload, MODAL_PROPS_PATH);

      // 'prioritize' will move the modal to the front of the queue, force the newly opened modal
      // to be shown, and the previous modal to be behind it in the queue. Once the new modal is closed
      // the previous modal will be shown again.
      const isPrioritized = get(action.payload, ['params', 'prioritize'], false);

      const modal = {
        ...action.payload,
      } as ModalInQueue;
      set(modal, MODAL_PROPS_PATH, undefined);

      /**
       * Reject duplicate modals
       *
       * Note: This isn't a totally thorough check. We'll still reject a modal as a duplicate even
       * if the callbacks are different as long as the type and text match!
       */
      const lastModal = selectLastModalFromQueue(draft);
      const lastModalProps = selectModalPropsForModal(lastModal);
      if (
        lastModal &&
        lastModalProps &&
        lastModal.type === modal.type &&
        lastModalProps.title === modalProps?.title &&
        isEqual(lastModalProps.subtitles, modalProps?.subtitles)
      ) {
        return;
      }

      const id = uuid();

      /**
       * The OPEN_MODAL action can contains props that are intended to pass directly to the
       * component,which can include callbacks. This is problematic because it means we can't
       * serialize them and put them in the store.
       *
       * As a workaround, we generate an ID for the modal before putting it in the store and then
       * save the props separately here. See modals/selectors.js for where this is actually stored.
       */
      setModalProps(id, modalProps);
      modal.id = id;

      if (isPrioritized) {
        draft.unshift(modal);
      } else {
        draft.push(modal);
      }

      break;
    }
    case CLOSE_MODAL: {
      const modal = draft[0];

      if (modal) {
        unsetModalProps(modal.id);
      }

      draft.shift();
      break;
    }
    default:
      break;
  }
}, initialState.queue);

export const reducer = combineReducers({
  queue: queueReducer,
});
